import {SHA3} from "sha3"
import {encode, Buffer, EncodeInput} from "@onflow/rlp"
import {sansPrefix} from "@onflow/util-address"

/**
 * Encodes a transaction payload for signing.
 *
 * This function takes a transaction object and encodes it into a format suitable for signing.
 * The encoded payload contains all the transaction details except for the signatures.
 *
 * @param tx The transaction object to encode
 * @returns A hex-encoded string representing the transaction payload
 *
 * @example
 * import * as fcl from "@onflow/fcl";
 * import { encodeTransactionPayload } from "@onflow/sdk"
 *
 * // Build a transaction
 * const transaction = await fcl.build([
 *   fcl.transaction`
 *     transaction(amount: UFix64) {
 *       prepare(account: AuthAccount) {
 *         log("Transferring: ".concat(amount.toString()))
 *       }
 *     }
 *   `,
 *   fcl.args([fcl.arg("10.0", fcl.t.UFix64)]),
 *   fcl.proposer(proposerAuthz),
 *   fcl.payer(payerAuthz),
 *   fcl.authorizations([authorizerAuthz]),
 *   fcl.limit(100)
 * ]);
 *
 * // Encode the transaction payload for signing
 * const encodedPayload = encodeTransactionPayload(transaction);
 * console.log("Encoded payload:", encodedPayload);
 * // Returns a hex string like "f90145b90140..."
 */
export const encodeTransactionPayload = (tx: Transaction) =>
  prependTransactionDomainTag(rlpEncode(preparePayload(tx)))

/**
 * Encodes a complete transaction envelope including payload and signatures.
 *
 * This function encodes the full transaction including both the payload and all signatures.
 * This is the final step before submitting a transaction to the Flow network.
 *
 * @param tx The transaction object to encode
 * @returns A hex-encoded string representing the complete transaction envelope
 *
 * @example
 * import * as fcl from "@onflow/fcl";
 * import { encodeTransactionEnvelope } from "@onflow/sdk"
 *
 * // Assuming you have a fully built and signed transaction
 * const signedTransaction = await fcl.build([
 *   fcl.transaction`
 *     transaction {
 *       prepare(account: AuthAccount) {
 *         log("Hello, Flow!")
 *       }
 *     }
 *   `,
 *   fcl.proposer(authz),
 *   fcl.payer(authz),
 *   fcl.authorizations([authz]),
 *   fcl.limit(100)
 * ]);
 *
 * // Add signatures to the transaction (this is usually done automatically)
 * // signedTransaction.payloadSigs = [...];
 * // signedTransaction.envelopeSigs = [...];
 *
 * // Encode the complete transaction envelope
 * const encodedEnvelope = encodeTransactionEnvelope(signedTransaction);
 * console.log("Encoded envelope:", encodedEnvelope);
 * // Returns a hex string ready for network submission
 */
export const encodeTransactionEnvelope = (tx: Transaction) =>
  prependTransactionDomainTag(rlpEncode(prepareEnvelope(tx)))

/**
 * Encodes a transaction ID from a voucher by computing its hash.
 *
 * A voucher is an intermediary object that contains transaction details before final encoding.
 * This function computes the transaction ID that would result from submitting the transaction.
 *
 * @param voucher The voucher object containing transaction details
 * @returns A hex-encoded string representing the transaction ID
 *
 * @example
 * import * as fcl from "@onflow/fcl";
 * import { encodeTxIdFromVoucher } from "@onflow/sdk"
 *
 * // Create a voucher (usually done internally by FCL)
 * const voucher = {
 *   cadence: `
 *     transaction {
 *       prepare(account: AuthAccount) {
 *         log("Hello")
 *       }
 *     }
 *   `,
 *   arguments: [],
 *   refBlock: "abc123...",
 *   computeLimit: 100,
 *   proposalKey: {
 *     address: "0x123456789abcdef0",
 *     keyId: 0,
 *     sequenceNum: 42
 *   },
 *   payer: "0x123456789abcdef0",
 *   authorizers: ["0x123456789abcdef0"],
 *   payloadSigs: [],
 *   envelopeSigs: []
 * };
 *
 * // Calculate the transaction ID
 * const txId = encodeTxIdFromVoucher(voucher);
 * console.log("Transaction ID:", txId);
 * // Returns a transaction ID that can be used to track the transaction
 */
export const encodeTxIdFromVoucher = (voucher: Voucher) =>
  sha3_256(rlpEncode(prepareVoucher(voucher)))

const rightPaddedHexBuffer = (value: string, pad: number) =>
  Buffer.from(value.padEnd(pad * 2, "0"), "hex")

const leftPaddedHexBuffer = (value: string, pad: number) =>
  Buffer.from(value.padStart(pad * 2, "0"), "hex")

const TRANSACTION_DOMAIN_TAG = rightPaddedHexBuffer(
  Buffer.from("FLOW-V0.0-transaction").toString("hex"),
  32
).toString("hex")
const prependTransactionDomainTag = (tx: string) => TRANSACTION_DOMAIN_TAG + tx

const addressBuffer = (addr: string) => leftPaddedHexBuffer(addr, 8)

const blockBuffer = (block: string) => leftPaddedHexBuffer(block, 32)

const argumentToString = (arg: Record<string, any>) =>
  Buffer.from(JSON.stringify(arg), "utf8")

const scriptBuffer = (script: string) => Buffer.from(script, "utf8")
const signatureBuffer = (signature: string) => Buffer.from(signature, "hex")
const extensionBuffer = (ext?: string) => {
  if (ext == null) return undefined
  return Buffer.from(ext, "hex")
}

const rlpEncode = (v: EncodeInput) => {
  return encode(v).toString("hex")
}

const sha3_256 = (msg: string) => {
  const sha = new SHA3(256)
  sha.update(Buffer.from(msg, "hex"))
  return sha.digest().toString("hex")
}

const preparePayload = (tx: Transaction) => {
  validatePayload(tx)

  return [
    scriptBuffer(tx.cadence || ""),
    tx.arguments.map(argumentToString),
    blockBuffer(tx.refBlock || ""),
    tx.computeLimit,
    addressBuffer(sansPrefix(tx.proposalKey.address || "")),
    tx.proposalKey.keyId,
    tx.proposalKey.sequenceNum,
    addressBuffer(sansPrefix(tx.payer)),
    tx.authorizers.map(authorizer => addressBuffer(sansPrefix(authorizer))),
  ]
}

const prepareEnvelope = (tx: Transaction) => {
  validateEnvelope(tx)

  return [preparePayload(tx), preparePayloadSignatures(tx)]
}

const preparePayloadSignatures = (tx: Transaction) => {
  const signers = collectSigners(tx)

  return tx.payloadSigs
    ?.map((sig: Sig) => {
      return {
        signerIndex: signers.get(sansPrefix(sig.address)) || "",
        keyId: sig.keyId,
        sig: sig.sig,
        sigExt: extensionBuffer(sig.extensionData),
      }
    })
    .sort((a, b) => {
      if (a.signerIndex > b.signerIndex) return 1
      if (a.signerIndex < b.signerIndex) return -1

      if (a.keyId > b.keyId) return 1
      if (a.keyId < b.keyId) return -1

      return 0
    })
    .map(sig => {
      const base: any[] = [sig.signerIndex, sig.keyId, signatureBuffer(sig.sig)]
      if (sig.sigExt != null) base.push(sig.sigExt)
      return base
    })
}

const collectSigners = (tx: Voucher | Transaction) => {
  const signers = new Map<string, number>()
  let i = 0

  const addSigner = (addr: string) => {
    if (!signers.has(addr)) {
      signers.set(addr, i)
      i++
    }
  }

  if (tx.proposalKey.address) {
    addSigner(tx.proposalKey.address)
  }
  addSigner(tx.payer)
  tx.authorizers.forEach(addSigner)

  return signers
}

const prepareVoucher = (voucher: Voucher) => {
  validateVoucher(voucher)

  const signers = collectSigners(voucher)

  const prepareSigs = (sigs: Sig[]) => {
    return sigs
      .map(({address, keyId, sig, extensionData}) => {
        return {
          signerIndex: signers.get(sansPrefix(address)) || "",
          keyId,
          sig,
          sigExt: extensionBuffer(extensionData),
        }
      })
      .sort((a, b) => {
        if (a.signerIndex > b.signerIndex) return 1
        if (a.signerIndex < b.signerIndex) return -1
        if (a.keyId > b.keyId) return 1
        if (a.keyId < b.keyId) return -1

        return 0
      })
      .map(sig => {
        const base: any[] = [
          sig.signerIndex,
          sig.keyId,
          signatureBuffer(sig.sig),
        ]
        if (sig.sigExt != null) base.push(sig.sigExt)
        return base
      })
  }

  return [
    [
      scriptBuffer(voucher.cadence),
      voucher.arguments.map(argumentToString),
      blockBuffer(voucher.refBlock),
      voucher.computeLimit,
      addressBuffer(sansPrefix(voucher.proposalKey.address)),
      voucher.proposalKey.keyId,
      voucher.proposalKey.sequenceNum,
      addressBuffer(sansPrefix(voucher.payer)),
      voucher.authorizers.map(authorizer =>
        addressBuffer(sansPrefix(authorizer))
      ),
    ],
    prepareSigs(voucher.payloadSigs),
    prepareSigs(voucher.envelopeSigs),
  ]
}

const validatePayload = (tx: Transaction) => {
  payloadFields.forEach(field => checkField(tx, field))
  proposalKeyFields.forEach(field =>
    checkField(tx.proposalKey, field, "proposalKey")
  )
}

const validateEnvelope = (tx: Transaction) => {
  payloadSigsFields.forEach(field => checkField(tx, field))
  tx.payloadSigs?.forEach((sig, index) => {
    payloadSigFields.forEach(field =>
      checkField(sig, field, "payloadSigs", index)
    )
  })
}

const validateVoucher = (voucher: Voucher) => {
  payloadFields.forEach(field => checkField(voucher, field))
  proposalKeyFields.forEach(field =>
    checkField(voucher.proposalKey, field, "proposalKey")
  )
  payloadSigsFields.forEach(field => checkField(voucher, field))
  voucher.payloadSigs.forEach((sig, index) => {
    payloadSigFields.forEach(field =>
      checkField(sig, field, "payloadSigs", index)
    )
  })
  envelopeSigsFields.forEach(field => checkField(voucher, field))
  voucher.envelopeSigs.forEach((sig, index) => {
    envelopeSigFields.forEach(field =>
      checkField(sig, field, "envelopeSigs", index)
    )
  })
}

const isNumber = (v: any): v is number => typeof v === "number"
const isString = (v: any): v is string => typeof v === "string"
const isObject = (v: any) => v !== null && typeof v === "object"
const isArray = (v: any) => isObject(v) && v instanceof Array

interface VoucherArgument {
  type: string
  value: string
}

interface VoucherProposalKey {
  address: string
  keyId: number | null
  sequenceNum: number | null
}

interface Sig {
  address: string
  keyId: number | string
  sig: string
  // Optional signature extension data (e.g., WebAuthn clientDataJSON + authenticatorData)
  // Propagated via "extensionData" when present by wallets/connectors.
  // Not required for existing signatures; encoding remains identical when absent.
  extensionData?: string
}

export interface TransactionProposalKey {
  address?: string
  keyId?: number | string
  sequenceNum?: number
}
export interface Transaction {
  cadence: string
  refBlock: string
  computeLimit: number
  arguments: VoucherArgument[]
  proposalKey: TransactionProposalKey
  payer: string
  authorizers: string[]
  payloadSigs?: Sig[]
  envelopeSigs?: TransactionProposalKey[]
}

export interface Voucher {
  cadence: string
  refBlock: string
  computeLimit: number
  arguments: VoucherArgument[]
  proposalKey: VoucherProposalKey
  payer: string
  authorizers: string[]
  payloadSigs: Sig[]
  envelopeSigs: Sig[]
}

interface PayloadField {
  name: string
  check: (v: any) => boolean
  defaultVal?: string
}

const payloadFields: PayloadField[] = [
  {name: "cadence", check: isString},
  {name: "arguments", check: isArray},
  {name: "refBlock", check: isString, defaultVal: "0"},
  {name: "computeLimit", check: isNumber},
  {name: "proposalKey", check: isObject},
  {name: "payer", check: isString},
  {name: "authorizers", check: isArray},
]

const proposalKeyFields: PayloadField[] = [
  {name: "address", check: isString},
  {name: "keyId", check: isNumber},
  {name: "sequenceNum", check: isNumber},
]

const payloadSigsFields: PayloadField[] = [
  {name: "payloadSigs", check: isArray},
]

const payloadSigFields: PayloadField[] = [
  {name: "address", check: isString},
  {name: "keyId", check: isNumber},
  {name: "sig", check: isString},
]

const envelopeSigsFields: PayloadField[] = [
  {name: "envelopeSigs", check: isArray},
]

const envelopeSigFields: PayloadField[] = [
  {name: "address", check: isString},
  {name: "keyId", check: isNumber},
  {name: "sig", check: isString},
]

const checkField = (
  obj: Record<string, any>,
  field: PayloadField,
  base?: string,
  index?: number
) => {
  const {name, check, defaultVal} = field
  if (obj[name] == null && defaultVal != null) obj[name] = defaultVal
  if (obj[name] == null) throw missingFieldError(name, base, index)
  if (!check(obj[name])) throw invalidFieldError(name, base, index)
}

const printFieldName = (field: string, base?: string, index?: number) => {
  if (!!base)
    return index == null ? `${base}.${field}` : `${base}.${index}.${field}`
  return field
}

const missingFieldError = (field: string, base?: string, index?: number) =>
  new Error(`Missing field ${printFieldName(field, base, index)}`)
const invalidFieldError = (field: string, base?: string, index?: number) =>
  new Error(`Invalid field ${printFieldName(field, base, index)}`)
